/** Keep layout steady through an outside click, including a held pointer. */
export function observeComposerOutsidePointer({
  target,
  contains,
  onPendingChange,
  onSettled,
}: {
  target: EventTarget;
  contains: (target: EventTarget | null) => boolean;
  onPendingChange: (pending: boolean) => void;
  onSettled: () => void;
}) {
  let pending = false;
  let release: ReturnType<typeof setTimeout> | undefined;
  const finish = () => {
    if (!pending) return;
    pending = false;
    clearTimeout(release);
    release = undefined;
    onPendingChange(false);
    onSettled();
  };
  const down = (event: Event) => {
    if (contains(event.target)) return;
    clearTimeout(release);
    pending = true;
    onPendingChange(true);
  };
  const up = () => {
    if (!pending) return;
    clearTimeout(release);
    // Click follows pointerup synchronously; cancellation may not produce a click.
    release = setTimeout(finish, 0);
  };
  target.addEventListener("pointerdown", down, true);
  target.addEventListener("pointerup", up, true);
  target.addEventListener("pointercancel", up, true);
  target.addEventListener("click", finish);
  return () => {
    target.removeEventListener("pointerdown", down, true);
    target.removeEventListener("pointerup", up, true);
    target.removeEventListener("pointercancel", up, true);
    target.removeEventListener("click", finish);
    clearTimeout(release);
    onPendingChange(false);
  };
}
